import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import classNames from 'classnames';

import RightMenu from '../../atoms/RightMenu/RightMenu';
import { exportConfigurationToPdf } from '../../Functions/ExportToPdf';
import { formatEntityId, formatOptionalEntityId } from '../../Functions/FormatEntityId';
import { getIconPath } from '../../Functions/GetIconPath';
import { setSelectedActuator } from '../../redux/reducers/actuatorsRedurer';
import { setSelectedAppliance } from '../../redux/reducers/applianceReducer';
import { 
    setApplianceLabel,
    setHoveredItem,
    setShowActuatorDetail,
    setShowApplianceDetail,
    setShowDetectorDetail,
    setShowDeviceDetail,
    setShowThermostatDetail,
    toggleRightMenu
} from '../../redux/reducers/appReducer';
import { setSelectedDeviceId } from '../../redux/reducers/controllersReducer';
import { setSelectedDetector } from '../../redux/reducers/detectorsReducer';
import { useAppDispatch, useAppSelector } from '../../redux/store';

import styles from '../../atoms/RightMenu/RightMenu.module.scss';

const RightMenuComponent: React.FC = () => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const [rightMenuOpen, setRightMenuOpen] = useState(false);
    const [editingApplianceId, setEditingApplianceId] = useState<number | null>(null);
    const [editValue, setEditValue] = useState('');
    const appliances = useAppSelector((state) => state.app.appliances);
    const controllers = useAppSelector((state) => state.controllers.devices);
    const actuators = useAppSelector((state) => state.app.actuators);
    const detectors = useAppSelector((state) => state.app.detectors);
    const [appliancesCollapsed, setAppliancesCollapsed] = useState(true);
    const [devicesCollapsed, setDevicesCollapsed] = useState(true);
    const [actuatorsCollapsed, setActuatorsCollapsed] = useState(true);
    const [detectorsCollapsed, setDetectorsCollapsed] = useState(true);

    const handleMenuClick = () => {
        dispatch(toggleRightMenu());
        setRightMenuOpen(!rightMenuOpen);
    };
    const showDeviceDetail = (deviceId: number, deviceType: string) => {
        dispatch(setSelectedDeviceId(deviceId));
        switch(deviceType){
            case 'thermostat':
                dispatch(setShowThermostatDetail(true));
            break;
            case 'controller':
                dispatch(setShowDeviceDetail(true));
            break;
            default:
                toast.error(t('rightMenu.devices.error'));
            break;
        }
    }
    const startEditLabel = (applianceId: number, currentLabel: string) => {
        setEditingApplianceId(applianceId);
        setEditValue(currentLabel);
    }
    const saveLabel = (applianceId: number) => {
        if (editingApplianceId !== applianceId) {
            return;
        }

        dispatch(setApplianceLabel({ id: applianceId, label: editValue }));
        setEditingApplianceId(null);
        setEditValue('');
    }
    const handleEditKeyDown = (event: React.KeyboardEvent<HTMLInputElement>, applianceId: number) => {
        if (event.key === 'Enter') {
            saveLabel(applianceId);
        }
    }
    const handleExportPdf = async () => {
        try {
            await exportConfigurationToPdf(controllers, actuators, detectors, appliances, {
                title: t('rightMenu.pdf.title'),
                controllersHeading: t('rightMenu.devices.heading'),
                actuatorsHeading: t('rightMenu.actuators.heading'),
                detectorsHeading: t('rightMenu.detectors.heading'),
                appliancesHeading: t('rightMenu.appliances.heading'),
                id: t('rightMenu.devices.header.ID'),
                type: t('rightMenu.devices.header.type'),
                name: t('rightMenu.pdf.name'),
                price: t('rightMenu.devices.header.price'),
                total: t('rightMenu.pdf.total'),
                noItems: t('rightMenu.pdf.noItems'),
                floorplanHeading: t('rightMenu.pdf.floorplanHeading')
            });
        } catch {
            toast.error(t('rightMenu.pdf.error'));
        }
    }

    return (
        <>
            <RightMenu rightMenuOpen={rightMenuOpen} onClick={handleMenuClick} onExport={handleExportPdf}>
                <div className={classNames(rightMenuOpen ? styles.rightMenu__content__appliances__open : styles.rightMenu__content__appliances__closed,styles.rightMenu__content__appliances)}>
                    <div className={styles.rightMenu__content__appliances__heading}>
                        <h3>{t('rightMenu.appliances.heading')}</h3>
                        <img src={getIconPath('arrows_up_down')} alt="Arrows" onClick={() => setAppliancesCollapsed(!appliancesCollapsed)}/>
                    </div>
                    {!appliancesCollapsed && (
                        <div className={styles.rightMenu__content__appliances__header}>
                            <div className={styles.rightMenu__content__appliances__header__item}><p>{t('rightMenu.appliances.header.state')}</p></div>
                            <div className={styles.rightMenu__content__appliances__header__item}><p>{t('rightMenu.appliances.header.ID')}</p></div>
                            <div className={styles.rightMenu__content__appliances__header__item}><p>{t('rightMenu.appliances.header.type')}</p></div>
                            <div></div>
                        </div>
                    )}
                    <RightMenu.Appliances rightMenuOpen={rightMenuOpen} collapsed={appliancesCollapsed}>
                        {appliances.map((item) => {
                            return (
                                <div
                                    className={styles.rightMenu__content__appliances__item}
                                    key={item.id}
                                    onMouseEnter={() => dispatch(setHoveredItem({ kind: 'appliance', id: item.id, source: 'menu' }))}
                                    onMouseLeave={() => dispatch(setHoveredItem(null))}
                                >
                                    <div className={styles.rightMenu__content__appliances__item__info}
                                        onClick={() => {dispatch(setSelectedAppliance({
                                            id: item.id,
                                            label: item.label,
                                            icon: item.icon,
                                            type: item.type,
                                            applianceType: item.applianceType,
                                            state: item.state ?? '',
                                            actuator: item.actuator ?? 0,
                                            connectedTo: item.connectedTo ?? [],
                                        }));
                                        dispatch(setShowApplianceDetail(true));}}
                                    >
                                        <img src={getIconPath(item.state || 'error')} alt="State" />
                                        <p>{formatEntityId('appliance', item.id)}</p>
                                        {editingApplianceId === item.id
                                            ? <input type='text' className={styles.rightMenu__content__appliances__item__input} autoFocus value={editValue} onChange={(e) => setEditValue(e.target.value)} onClick={(e) => e.stopPropagation()} onKeyDown={(e) => handleEditKeyDown(e, item.id)} onBlur={() => saveLabel(item.id)} />
                                            : <p>{item.label}</p>
                                        }
                                    </div>
                                    <img
                                        className={styles.rightMenu__content__appliances__item__edit}
                                        onClick={() => editingApplianceId === item.id ? saveLabel(item.id) : startEditLabel(item.id, item.label)}
                                        src={getIconPath('icoPen')}
                                        alt="Edit"
                                    />
                                </div>
                            );
                        })}
                    </RightMenu.Appliances>
                </div>
                <div className={classNames(rightMenuOpen ? styles.rightMenu__content__devices__open : styles.rightMenu__content__devices__closed,styles.rightMenu__content__devices)}>
                    <div className={styles.rightMenu__content__devices__heading}>
                        <h3>{t('rightMenu.devices.heading')}</h3>
                        <img src={getIconPath('arrows_up_down')} alt="Arrows" onClick={() => setDevicesCollapsed(!devicesCollapsed)} />
                    </div>
                    {!devicesCollapsed && (
                        <div className={styles.rightMenu__content__devices__header}>
                            <div className={styles.rightMenu__content__devices__header__item}><p>{t('rightMenu.devices.header.state')}</p></div>
                            <div className={styles.rightMenu__content__devices__header__item}><p>{t('rightMenu.devices.header.ID')}</p></div>
                            <div className={styles.rightMenu__content__devices__header__item}><p>{t('rightMenu.devices.header.type')}</p></div>
                            <div className={styles.rightMenu__content__devices__header__item}><p>{t('rightMenu.devices.header.price')}</p></div>
                        </div>
                    )}
                    <RightMenu.Devices rightMenuOpen={rightMenuOpen} collapsed={devicesCollapsed}>
                        {controllers.map((item) => {
                            return (
                                <div
                                    onClick={() => showDeviceDetail(item.id, item.type)}
                                    className={styles.rightMenu__content__devices__item}
                                    key={item.id}
                                    onMouseEnter={() => dispatch(setHoveredItem({ kind: 'controller', id: item.id, source: 'menu' }))}
                                    onMouseLeave={() => dispatch(setHoveredItem(null))}
                                >
                                    <img src={getIconPath(item.state || 'error')} alt="State" />
                                    <p>{formatEntityId('controller', item.id)}</p>
                                    <p>{item.name + item.colorShortcut}</p>
                                    <p>{item.price || ''} {item.price ? '€' : ''}</p>
                                </div>
                            )
                        })}
                    </RightMenu.Devices>
                </div>
                <div className={classNames(rightMenuOpen ? styles.rightMenu__content__appliances__open : styles.rightMenu__content__appliances__closed,styles.rightMenu__content__appliances)}>
                    <div className={styles.rightMenu__content__appliances__heading}>
                        <h3>{t('rightMenu.actuators.heading')}</h3>
                        <img src={getIconPath('arrows_up_down')} alt="Arrows" onClick={() => setActuatorsCollapsed(!actuatorsCollapsed)}/>
                    </div>
                    {!actuatorsCollapsed && (
                        <div className={styles.rightMenu__content__actuators__header}>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.actuators.header.state')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.actuators.header.ID')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.actuators.header.type')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.actuators.header.price')}</p></div>
                        </div>
                    )}
                    <RightMenu.Actuators rightMenuOpen={rightMenuOpen} collapsed={actuatorsCollapsed}>
                        {actuators.map((item) => {
                            return (
                                <div
                                    onClick={() => {dispatch(setSelectedActuator(item));dispatch(setShowActuatorDetail(true));}}
                                    className={styles.rightMenu__content__actuators__item}
                                    key={item.id}
                                    onMouseEnter={() => dispatch(setHoveredItem({ kind: 'actuator', id: item.id || 0, source: 'menu' }))}
                                    onMouseLeave={() => dispatch(setHoveredItem(null))}
                                >
                                    <img src={getIconPath(item.state || 'error')} alt="State" />
                                    <p>{formatOptionalEntityId('actuator', item.id)}</p>
                                    <p>{item.type}</p>
                                    <p>{item.price || ''} {item.price ? '€' : ''}</p>
                                </div>
                            )
                        })}
                    </RightMenu.Actuators>
                </div>
                <div className={classNames(rightMenuOpen ? styles.rightMenu__content__appliances__open : styles.rightMenu__content__appliances__closed,styles.rightMenu__content__appliances)}>
                    <div className={styles.rightMenu__content__appliances__heading}>
                        <h3>{t('rightMenu.detectors.heading')}</h3>
                        <img src={getIconPath('arrows_up_down')} alt="Arrows" onClick={() => setDetectorsCollapsed(!detectorsCollapsed)}/>
                    </div>
                    {!detectorsCollapsed && (
                        <div className={styles.rightMenu__content__actuators__header}>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.detectors.header.state')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.detectors.header.ID')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.detectors.header.type')}</p></div>
                            <div className={styles.rightMenu__content__actuators__header__item}><p>{t('rightMenu.detectors.header.price')}</p></div>
                        </div>
                    )}
                    <RightMenu.Detectors rightMenuOpen={rightMenuOpen} collapsed={detectorsCollapsed}>
                        {detectors.map((item) => {
                            return (
                                <div
                                    onClick={() => {dispatch(setSelectedDetector(item));dispatch(setShowDetectorDetail(true));}}
                                    className={styles.rightMenu__content__actuators__item}
                                    key={item.id}
                                    onMouseEnter={() => dispatch(setHoveredItem({ kind: 'detector', id: item.id || 0, source: 'menu' }))}
                                    onMouseLeave={() => dispatch(setHoveredItem(null))}
                                >
                                    <img src={getIconPath(item.state || 'error')} alt="State" />
                                    <p>{formatOptionalEntityId('detector', item.id)}</p>
                                    <p>{item.name}</p>
                                    <p>{item.price || ''} {item.price ? '€' : ''}</p>
                                </div>
                            )
                        })}
                    </RightMenu.Detectors>
                </div>
            </RightMenu>
        </>
    )
};

export default RightMenuComponent;